<template>
  <div>
    <v-form ref="form" v-model="valid">
      <v-row>
        <v-col cols="12" md="8">
          <v-row>
            <v-col cols="12" class="filters-container d-flex justify-space-between" :class="{ 'flex-wrap': $vuetify.breakpoint.mobile }">
              <v-col cols="6">
                <v-row>
                  <v-col cols="6">
                    <label for="vehicleType">{{ $t('filters.bodyType') }}</label>
                    <v-select
                      v-model="filters.type.value"
                      id="vehicleType"
                      :items="vehicleTypes"
                      item-text="translatedValues[0].name"
                      item-value="id"
                      outlined
                      required
                      validate-on-blur
                      dense
                      single-line
                      hide-details
                      multiple
                    />
                  </v-col>
                  <v-col cols="6">
                    <label for="vehicleSizeType">{{ $t('filters.sizeType') }}</label>
                    <v-select
                      v-model="filters.size.value"
                      id="vehicleSizeType"
                      :items="vehicleSizeTypes"
                      item-text="description"
                      item-value="id"
                      outlined
                      required
                      validate-on-blur
                      dense
                      single-line
                      hide-details
                      multiple
                    />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="6" class="pt-4 filters-container d-flex justify-space-between" :class="{ 'flex-wrap': $vuetify.breakpoint.mobile }">
                    <v-btn color="primary" outlined block style="margin:0px; padding:0px;" @click="displayMoreFiltersDialog = true">
                      {{ $t('filters.more') }}
                    </v-btn>
                  </v-col>

                  <template v-if="freightMode">
                    <v-col cols="3" class="pt-0" xs="6" sm="6" md="3">
                      <label for="minFreightWeight">{{ $t('filters.weightFrom') }}</label>
                      <v-text-field
                        v-model.number="filters.minFreightWeight.value"
                        id="minFreightWeight"
                        type="number"
                        outlined
                        required
                        validate-on-blur
                        dense
                        single-line
                        hide-details
                        :rules="[validations.onlyNaturalNumbers, validations.freightLoad]"
                      />
                    </v-col>
                    <v-col cols="3" class="pt-0" xs="6" sm="6" md="3">
                      <label for="maxFreightWeight">{{ $t('filters.weightTo') }}</label>
                      <v-text-field
                        v-model.number="filters.maxFreightWeight.value"
                        id="maxFreightWeight"
                        type="number"
                        outlined
                        required
                        validate-on-blur
                        dense
                        single-line
                        hide-details
                        :rules="[validations.onlyNaturalNumbers, validations.freightLoad]"
                      />
                    </v-col>
                  </template>
                  <template v-else>
                    <v-col cols="3" class="pt-0">
                      <label for="minLoad">{{ $t('filters.weightVehicleFrom') }}</label>
                      <v-text-field
                        v-model.number="filters.minLoad.value"
                        id="minLoad"
                        type="number"
                        outlined
                        required
                        validate-on-blur
                        dense
                        single-line
                        hide-details
                        :rules="[validations.onlyNaturalNumbers, validations.vehicleLoad]"
                      />
                    </v-col>
                    <v-col cols="3" class="pt-0">
                      <label for="maxLoad">{{ $t('filters.weightVehicleTo') }}</label>
                      <v-text-field
                        v-model.number="filters.maxLoad.value"
                        id="maxLoad"
                        type="number"
                        outlined
                        required
                        validate-on-blur
                        dense
                        single-line
                        hide-details
                        :rules="[validations.onlyNaturalNumbers, validations.vehicleLoad]"
                      />
                    </v-col>
                  </template>
                </v-row>

                <v-row>
                  <v-col cols="6" class="filters-container d-flex justify-space-between" :class="{ 'flex-wrap': $vuetify.breakpoint.mobile }">
                    <v-btn color="primary" depressed block @click="apply">
                      {{ $t('filters.setFilter') }}
                    </v-btn>
                  </v-col>
                  <v-col v-if="freightMode" cols="6" class="pt-4">
                    <v-checkbox v-model="filters.cabotage.value" class="pa-0 ma-0" hide-details>
                      <template #label>
                        <span>{{ $t('filters.loadsDialog.cabotage') }}</span>
                      </template>
                    </v-checkbox>
                  </v-col>
                </v-row>
              </v-col>

              <v-col cols="6">
                <v-row v-if="!hideDateFilters">
                  <v-col cols="12" md="6">
                    <DatePicker v-model="filters.minStart.value" :label="$t('filters.loadDateFrom')" :rules="[validations.start]" />
                  </v-col>
                  <v-col cols="12" md="6">
                    <DatePicker v-model="filters.maxStart.value" :label="$t('filters.loadDateTo')" :min="filters.minStart.value" :rules="[validations.start]" />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="pt-0">
                    <label>{{ $t('filters.load') }}</label>
                    <LocationInput
                      v-for="(location, index) in filters.locations.loadingPlaces"
                      :key="`load-${index}`"
                      v-model="location.value"
                      clearable
                      @removeItem="locationRemoveLoadInputHandler"
                    />
                    <v-btn v-if="!disabledAddLoadingPlaces" color="primary" class="mt-3" outlined :disabled="disabledAddLoadingPlaces" @click="addLoadLocationClick">
                      {{ $t('filters.add') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-col>

              <v-col cols="6">
                <v-row v-if="!hideDateFilters">
                  <v-col cols="12" md="6">
                    <DatePicker v-model="filters.minEnd.value" :label="$t('filters.unloadDateFrom')" :rules="[validations.end]" />
                  </v-col>
                  <v-col cols="12" md="6">
                    <DatePicker v-model="filters.maxEnd.value" :label="$t('filters.unloadDateTo')" :min="filters.minEnd.value" :rules="[validations.end]" />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="pt-0">
                    <label>{{ $t('filters.unload') }}</label>
                    <LocationInput
                      v-for="(location, index) in filters.locations.unloadingPlaces"
                      :key="`unload-${index}`"
                      v-model="location.value"
                      clearable
                      @removeItem="locationRemoveUnloadInputHandler"
                    />
                    <v-btn v-if="!disabledAddUnloadingPlaces" color="primary" class="mt-3" outlined :disabled="disabledAddUnloadingPlaces" @click="addUnloadLocationClick">
                      {{ $t('filters.add') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-col>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-form>

    <MoreFiltersDialog
      v-if="displayMoreFiltersDialog"
      v-model="displayMoreFiltersDialog"
      :data="filters"
      :freight-mode-override="freightMode"
      :hide-market-options="true"
      @change-filters="changeFilters"
    />
  </div>
</template>

<script>
import Service from '@/services'
import { LOCATIONCROSS } from '@/types/condition-types'
import { convertDataToFilters, convertFilters, createNewPlace } from '@/utils/market'
import LocationInput from '@/components/forms/LocationInput'
import DatePicker from '@/components/forms/DatePicker'
import moment from 'moment'
import { SPEDILINK_LINK_TYPES } from '@/views/Spedilink/spedilink-constants'

export default {
  name: 'SpedilinkDetailsFilter',
  components: {
    MoreFiltersDialog: () => import('@/views/Market/dialogs/MoreFiltersDialog'),
    LocationInput,
    DatePicker
  },
  props: {
    type: {
      type: Number,
      required: true
    },
    isArchiveTab: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      companies: [],
      companiesGroups: [],
      valid: true,
      displayMoreFiltersDialog: false,
      vehicleTypes: [],
      validations: {
        onlyNaturalNumbers: (v) => v ? v > 0 : true,
        freightLoad: () => (this.filters.minFreightWeight.value && this.filters.maxFreightWeight.value) ? this.filters.minFreightWeight.value <= this.filters.maxFreightWeight.value : true,
        vehicleLoad: () => (this.filters.minLoad.value && this.filters.maxLoad.value) ? this.filters.minLoad.value <= this.filters.maxLoad.value : true,
        start: () => (this.filters.minStart.value && this.filters.minStart.value.length > 0 && this.filters.maxStart.value && this.filters.maxStart.value.length > 0) ? moment(this.filters.minStart.value).unix() > moment(this.filters.maxStart.value).unix() ? this.$t('validation.dateRange') : true : true,
        end: () => (this.filters.minEnd.value && this.filters.minEnd.value.length > 0 && this.filters.maxEnd.value && this.filters.maxEnd.value.length > 0) ? moment(this.filters.minEnd.value).unix() > moment(this.filters.maxEnd.value).unix() ? this.$t('validation.dateRange') : true : true
      },
      filters: convertDataToFilters(null)
    }
  },
  computed: {
    vehicleSizeTypes () {
      return this.$store.getters.enums.vehicleSizeTypes
    },
    freightMode () {
      return this.type === SPEDILINK_LINK_TYPES.FREIGHT
    },
    // Tymczasowe obejście błędu w filtrowaniu ładunków po dacie w bazie danych (zakładka Archiwum)
    hideDateFilters () {
      return this.freightMode && this.isArchiveTab
    },
    disabledAddLoadingPlaces () {
      return this.filters.locations.loadingPlaces.length > 4
    },
    disabledAddUnloadingPlaces () {
      return this.filters.locations.unloadingPlaces.length > 4
    },
    filterCompanies: {
      get () {
        return this.filters.group.value?.companiesIds
      },
      set (val) {
        if (this.filters.group.value != null) {
          this.filters.group.value.companiesIds = val
        } else {
          this.filters.group.value = {
            companiesIds: val,
            groupCompanies: []
          }
        }
        if (val.length === 0) {
          if (this.filters.group.value.groupCompanies == null || this.filters.group.value.groupCompanies.length === 0) {
            this.filters.group.value = null
          }
        }
      }
    },
    filterGroups: {
      get () {
        const groups = this.companiesGroups
        return this.filters.group.value?.groupCompanies?.map(x => ({
          id: x.id,
          name: groups.find(y => y.id === x.id)?.name
        }))
      },
      set (val) {
        const result = []
        for (const x of val) {
          const group = this.companiesGroups.find(y => y.id === x)
          if (group && !result.some(y => y.id === group.id)) {
            result.push({ id: group.id })
          }
        }
        if (this.filters.group.value != null) {
          this.filters.group.value.groupCompanies = result
        } else {
          this.filters.group.value = {
            companiesIds: [],
            groupCompanies: result
          }
        }
        if (val.length === 0) {
          if (this.filters.group.value.companiesIds == null || this.filters.group.value.companiesIds.length === 0) {
            this.filters.group.value = null
          }
        }
      }
    }
  },
  async created () {
    await this.fetchData()
    this.filters = convertDataToFilters(null)
  },
  methods: {
    async fetchData () {
      try {
        const { data: vehicleTypes } = await Service.get('Dictionary/GetVehicleTypes')
        this.vehicleTypes = vehicleTypes
        this.companies = (await Service.get('Company/GetCompanyNames')).data
        const companiesGroupResponse = await Service.get('CompanyGroup/GetAll?page=1&pageSize=1000')
        this.companiesGroups = companiesGroupResponse.data.results
      } catch (e) {
      }
    },
    apply () {
      if (!this.$refs.form.validate()) {
        return
      }
      const conditions = {
        ...this.filters,
        locations: {
          ...this.filters.locations,
          loadingPlaces: this.filters.locations.loadingPlaces.map(i => ({ ...i.value })),
          unloadingPlaces: this.filters.locations.unloadingPlaces.map(i => ({ ...i.value }))
        }
      }
      this.$emit('apply', convertFilters(this.type, conditions).conditions)
    },
    clearFilters () {
      this.filters = convertDataToFilters(null)
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.resetValidation()
        }
      })
      this.$emit('apply', [])
    },
    changeFilters (filters) {
      this.filters = filters
      this.apply()
    },
    addLoadLocationClick () {
      this.filters.locations.loadingPlaces.push({ value: createNewPlace() })
      this.filters.locations.type = LOCATIONCROSS
    },
    addUnloadLocationClick () {
      this.filters.locations.unloadingPlaces.push({ value: createNewPlace() })
      this.filters.locations.type = LOCATIONCROSS
    },
    locationRemoveLoadInputHandler (value) {
      this.locationRemoveInputHandler(0, value)
    },
    locationRemoveUnloadInputHandler (value) {
      this.locationRemoveInputHandler(1, value)
    },
    locationRemoveInputHandler (type, value) {
      if (value.country == null) {
        let indexToRemove = -1
        const collection = type === 0
          ? this.filters.locations.loadingPlaces
          : this.filters.locations.unloadingPlaces

        collection.forEach(function (x, index) {
          if (indexToRemove !== -1) { return }
          indexToRemove = (collection.length === 2 || index > 0) && x.value.country == null ? index : -1
        })

        if (indexToRemove >= 0) {
          collection.splice(indexToRemove, 1)
        }
      }
    }
  }
}
</script>

<style scoped>
</style>
